// components/exercises/lesson/NoteIdentification.tsx
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Box, Button, Text } from "@chakra-ui/react";
import DynamicPiano, {
  DynamicPianoHandle,
} from "components/exercises/DynamicPiano";
import { ExerciseDefinition } from "utils/exercises/types";
import { useSound } from "providers/SoundProvider";
import PlayButton from "components/PlayButton";

interface NoteIdentificationProps {
  exercise: ExerciseDefinition;
  onResult: (isCorrect: boolean) => void;
}

const NoteIdentification = forwardRef((props: NoteIdentificationProps, ref) => {
  const { exercise, onResult } = props;
  const pianoRef = useRef<DynamicPianoHandle>(null);
  const { playNote, isAudioPlaying, setIsAudioPlaying } = useSound();
  const [interactionAllowed, setInteractionAllowed] = useState(true); // State to manage interaction with the piano

  const handlePlayNote = async () => {
    if (!isAudioPlaying) {
      if (exercise.data.notes) {
        setIsAudioPlaying(true);

        const firstNote = exercise.data.notes[0];
        playNote(firstNote);

        await new Promise((resolve) => setTimeout(resolve, 3000));
        setIsAudioPlaying(false);
      }
    }
  };

  useEffect(() => {
    setInteractionAllowed(true);
    if (!isAudioPlaying) {
      handlePlayNote();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exercise.data.notes]); // Re-run when notes change

  useImperativeHandle(ref, () => ({
    checkAnswer: () => localCheckAnswer(),
  }));

  const localCheckAnswer = () => {
    if (pianoRef.current && exercise.data.notes) {
      const getOriginalNotes = exercise.data.notes;
      // Ensures that exercise.data.notes is defined and is an array before sorting
      const userNotes = pianoRef.current.getUserSelectedNotes();
      const sortedUserNotes = userNotes.sort((a, b) => a - b);
      const sortedExerciseNotes = [...getOriginalNotes].sort((a, b) => a - b); // Clone and sort to prevent mutating original data
      const isCorrect =
        userNotes.length === sortedExerciseNotes.length &&
        sortedUserNotes.every(
          (note, index) => note === sortedExerciseNotes[index]
        );
      onResult(isCorrect); // Send the result to the parent component via onResult
      setInteractionAllowed(false); // Disable interaction after checking the answer
    }
  };

  return (
    <Box textAlign="center" width={"100%"}>
      <Text fontSize="xl" fontWeight={500} mb={4}>
        Listen to the note and identify it on the piano
      </Text>
      <PlayButton label="Play Note" onClick={handlePlayNote} />
      <Box mb={6}></Box>

      <DynamicPiano
        ref={pianoRef}
        exerciseDefinition={exercise}
        disabled={!interactionAllowed}
        correctNotes={exercise.data.notes || []}
      />
    </Box>
  );
});

export default NoteIdentification;
